import { useTranslate } from '@festina-life/ui/hooks'
import type { SearchMatch } from '@festina-life/ui/search'
import { useCallback, useMemo } from 'react'

import { enumLiteralPath } from '../../system/paths'
import type { EnumDefinition } from '../../uiModels/enumLiterals/types'
import { useOperationsContext } from '../../workspace/operationsContext'
import { configurationItemsMapToList } from '../errorsPanel/helpers'
import { groupSearchResults } from './helpers'
import {
  type ConfigurationItemSearchFunctions,
  type ConfigurationItemSearchMatch,
  type ConfigurationItemSearchResult,
  SearchCategories,
  type SearchCategoryKey,
} from './types'

const toConfigurationItemSearchResult =
  (
    packageName: string,
    matches?: SearchMatch<ConfigurationItemSearchMatch, SearchCategoryKey>[],
  ) =>
  (definition: EnumDefinition): ConfigurationItemSearchResult => ({
    value: {
      id: definition.name,
      type: null,
      operationType: 'enumDefinition',
      packageName,
      layer: definition.layer,
      block: definition.block,
      isDraft:
        definition.isDraft ||
        definition.literals.some(
          (literal) => literal.type === 'configured' && literal.isDraft,
        ),
    },
    matches,
  })

export const useEnumSearch = (): ConfigurationItemSearchFunctions => {
  const { configuredEnums } = useOperationsContext()
  const translate = useTranslate()
  const allConfiguredEnums = useMemo(
    () =>
      configurationItemsMapToList(configuredEnums).filter(
        (indexedEnum) => !indexedEnum.annulOperations.length,
      ),
    [configuredEnums],
  )

  const searchByName = useCallback(
    (query: string) => {
      const needle = query.toLowerCase().trim()

      return Promise.resolve(
        allConfiguredEnums
          .filter((enumDefinition) =>
            enumDefinition.name.toLocaleLowerCase().includes(needle),
          )
          .map(
            toConfigurationItemSearchResult(
              translate('enumsTree.fallbackPackage.enums'),
            ),
          ),
      )
    },
    [allConfiguredEnums, translate],
  )

  const searchByProperties = useCallback(
    (query: string) => {
      const needle = query.toLowerCase().trim()

      return Promise.resolve(
        allConfiguredEnums
          .map(
            (configuredEnum) =>
              [
                configuredEnum,
                configuredEnum.literals
                  .filter(
                    (literal) =>
                      literal.type === 'modeled' ||
                      (literal.type === 'configured' &&
                        !literal.annulOperations.length),
                  )
                  .map((literal) =>
                    literal.type === 'modeled'
                      ? literal.literalName
                      : (literal.internalOperation.entity?.name ?? ''),
                  )
                  .filter((literalName) =>
                    literalName.toLowerCase().includes(needle),
                  )
                  .map(
                    (
                      literalName,
                    ): SearchMatch<
                      ConfigurationItemSearchMatch,
                      SearchCategoryKey
                    > => ({
                      categories: [SearchCategories.properties],
                      path: `${configuredEnum.name}.${literalName}`,
                      value: {
                        isExpression: false,
                        match: literalName,
                        propertyPath: enumLiteralPath({
                          enumName: configuredEnum.name,
                          literalName,
                        }),
                      },
                    }),
                  ),
              ] as const,
          )
          .filter(([, matches]) => !!matches.length)
          .map(([enumDefinition, matches]) =>
            toConfigurationItemSearchResult(
              translate('enumsTree.fallbackPackage.enums'),
              matches,
            )(enumDefinition),
          ),
      )
    },
    [allConfiguredEnums, translate],
  )

  const searchByFPlus = useCallback(() => {
    throw new Error('Searching FPlus within enums is unavailable')
  }, [])

  const searchAll = useCallback(
    async (query: string) => {
      const allByName = await searchByName(query)
      const allByProperties = await searchByProperties(query)

      return groupSearchResults(...allByName, ...allByProperties)
    },
    [searchByName, searchByProperties],
  )

  return {
    name: searchByName,
    className: searchByName,
    fplus: searchByFPlus,
    properties: searchByProperties,
    all: searchAll,
  }
}
